/**
 * ShortcutButtonBlock
 */

import Icon from 'components/Icon';
import BlockContainer from 'layouts/BlockLayout';
import React from 'react';
import { colors } from 'theme/colors';
import { LinkType } from 'types/global';
import { P } from 'components/Text/Text.styles';
import {
	Container,
	Link,
	LinksContainer,
	StyledP,
	StyledH2,
} from './ShortcutButtonBlock.styles';

interface Props {
	/** Block title */
	heading?: string;

	/** Block description */
	preamble?: string;

	/** background color for the block */
	backgroundColor?: string;

	/** background color for shortcut buttons */
	shortcutColor?: string;

	/** list of buttons */
	links?: LinkType[];
}

/** Main description for this component. */
const ShortcutButtonBlock: React.FC<Props> = ({
	heading = '',
	preamble = '',
	backgroundColor = 'white',
	shortcutColor = 'white',
	links = [],
}) => {
	const getShortcutButtonColor = (color: string) => {
		switch (color) {
			case 'white':
				return {
					backgroundColor: colors.white,
					color: colors.linkColorDark,
					borderColor: colors.linkColorDark,
				};
			case 'greyLighter':
				return { backgroundColor: colors.greyLighter, color: colors.greyDark };
			case 'yellow':
				return {
					backgroundColor: colors.yellow,
					color: colors.greyDark,
				};
			case 'blue':
				return { backgroundColor: colors.blueDark, color: colors.white };
			case 'darkBlue':
				return { backgroundColor: colors.blueDarker, color: colors.white };
			case 'lightBlue':
				return { backgroundColor: colors.blueLightest, color: colors.greyDark };
			default:
				return { backgroundColor: colors.white, color: colors.greyDark };
		}
	};

	const mapBlockBackgroundColor = (color: string): string => {
		switch (color) {
			case 'yellow':
				return 'yellowLighter';
			case 'greyLighter':
				return 'greyLighter';
			case 'blue':
				return 'blueDark';
			case 'darkBlue':
				return 'blueDarker';
			case 'lightBlue':
				return 'blueLightest';
			case 'white':
			default:
				return 'white';
		}
	};

	const getTextColorForBackground = (backgroundColor: string): string => {
		switch (backgroundColor) {
			case 'yellow':
				return colors.greyDark;
			case 'blue':
				return colors.white;
			case 'greyLighter':
				return colors.greyDark;
			case 'darkBlue':
				return colors.white;
			case 'lightBlue':
				return colors.greyDark;
			case 'white':
			default:
				return colors.greyDark;
		}
	};

	const mappedBackgroundColor = mapBlockBackgroundColor(backgroundColor);
	const textColor = getTextColorForBackground(backgroundColor);

	return (
		<BlockContainer backgroundColor={mappedBackgroundColor as any}>
			<Container>
				<StyledH2
					hasPreamble={!!preamble && preamble.length > 0}
					style={{ color: textColor }}
				>
					{heading}
				</StyledH2>
				{preamble && <P style={{ color: textColor }}>{preamble}</P>}
				<LinksContainer>
					{links &&
						links.length > 0 &&
						links.map((link, index) => (
							<Link
								key={index}
								href={link.link}
								target={link.target ? link.target : '_self'}
								rel={link.target === '_blank' ? 'noopener noreferrer' : ''}
								{...getShortcutButtonColor(shortcutColor)}
							>
								<StyledP>{link.text}</StyledP>
								<Icon
									icon="arrowRight"
									size={0}
									className="shortcut-button-icon"
									color="currentColor"
								/>
							</Link>
						))}
				</LinksContainer>
			</Container>
		</BlockContainer>
	);
};

export default ShortcutButtonBlock;
